import React from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Chip,
  Paper,
} from "@mui/material";
import { Link } from "react-router-dom";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import MessageIcon from "@mui/icons-material/Message";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PaymentIcon from "@mui/icons-material/Payment";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import WarningIcon from "@mui/icons-material/Warning";
import ChecklistIcon from "@mui/icons-material/Checklist";

const FeaturesOverview = () => {
  const theme = useTheme();

  const features = [
    {
        title: "Bring Your Cleaner",
        description: "Keep your trusted team. Invite them in seconds via email, text, or QR code.",
        icon: <PersonAddIcon sx={{ fontSize: 40 }} />,
        color: "#f093fb",
        gradient: "linear-gradient(135deg, #f093fb, #f5576c)",
        link: "/features/bring-your-cleaner",
        badge: "Host Favorite",
    },
    {
      title: "Set Your Own Price",
      description: "Control cleaning fees per property. Maximize revenue during peak season.",
      icon: <PriceChangeIcon sx={{ fontSize: 40 }} />,
      color: "#43e97b",
      gradient: "linear-gradient(135deg, #43e97b, #38f9d7)",
      link: "/features/set-your-price",
      badge: "Host Favorite",
    },
    {
        title: "Incident Report",
        description: "Document damage instantly with geo-tagged photos. Airbnb-ready reports for disputes.",
        icon: <WarningIcon sx={{ fontSize: 40 }} />,
        color: "#ff6b6b",
        gradient: "linear-gradient(135deg, #ff6b6b, #ee5a24)",
        link: "/features/incident-report",
        badge: "Host Protection",
      },
    {
      title: "Smart Scheduling",
      description: "Auto-schedule cleanings after every checkout. Never miss a turnover.",
      icon: <ScheduleIcon sx={{ fontSize: 40 }} />,
      color: "#667eea",
      gradient: "linear-gradient(135deg, #667eea, #764ba2)",
      link: "/features/smart-scheduling",
    },
    {
      title: "Photo Verification",
      description: "Time-stamped before/after photos. Know exactly when it's ready.",
      icon: <PhotoCameraIcon sx={{ fontSize: 40 }} />,
      color: "#f093fb",
      gradient: "linear-gradient(135deg, #f093fb, #f5576c)",
      link: "/features/photo-verification",
    },
    {
      title: "In-App Messaging",
      description: "Chat with cleaners, share access codes, resolve issues fast.",
      icon: <MessageIcon sx={{ fontSize: 40 }} />,
      color: "#4facfe",
      gradient: "linear-gradient(135deg, #4facfe, #00f2fe)",
      link: "/features/in-app-messaging",
    },
    {
      title: "Multi-Property Dashboard",
      description: "Manage all properties from one central hub. Real-time updates.",
      icon: <DashboardIcon sx={{ fontSize: 40 }} />,
      color: "#fa709a",
      gradient: "linear-gradient(135deg, #fa709a, #fee140)",
      link: "/features/multi-property-dashboard",
    },
    {
      title: "Custom Cleaning Checklists",
      description: "Create room-by-room cleaning standards. Require photo proof for important tasks.",
      icon: <ChecklistIcon sx={{ fontSize: 40 }} />,
      color: "#667eea",
      gradient: "linear-gradient(135deg, #667eea, #764ba2)",
      link: "/features/custom-checklist",
      badge: "Quality Control",
    },
    {
      title: "Flexible Payouts",
      description: "Instant, weekly, or monthly. Pay cleaners your way.",
      icon: <PaymentIcon sx={{ fontSize: 40 }} />,
      color: "#a8edea",
      gradient: "linear-gradient(135deg, #a8edea, #fed6e3)",
      link: "/features/flexible-payouts",
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.dark, 0.02)} 100%)`,
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 10 },
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Chip
            label="✨ Powerful Tools"
            sx={{
              mb: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              fontWeight: 600,
            }}
          />
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            Everything You Need to Scale
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: "text.secondary",
              maxWidth: "700px",
              mx: "auto",
              mb: 4,
            }}
          >
            Purpose-built features for hosts who want control, automation, and peace of mind
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AutoAwesomeIcon />}
            sx={{
              borderRadius: "50px",
              px: 5,
              py: 1.5,
              fontSize: "1.1rem",
              fontWeight: 700,
            }}
          >
            Start Free Trial
          </Button>
        </Container>
      </Box>

      {/* Features Grid */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                component={Link}
                to={feature.link}
                sx={{
                  height: "100%",
                  borderRadius: 4,
                  transition: "all 0.3s ease",
                  textDecoration: "none",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: theme.shadows[8],
                  },
                  position: "relative",
                  overflow: "visible",
                }}
              >
                {feature.badge && (
                  <Chip
                    label={feature.badge}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: -12,
                      right: 16,
                      bgcolor: theme.palette.success.main,
                      color: "white",
                      fontWeight: 700,
                      zIndex: 1,
                    }}
                  />
                )}
                <CardActionArea>
                  <CardContent sx={{ p: 4, textAlign: "center" }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "24px",
                        background: feature.gradient,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 16px",
                        color: "white",
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" fontWeight="800" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {feature.description}
                    </Typography>
                    <Button
                      endIcon={<ArrowForwardIcon />}
                      sx={{ fontWeight: 600 }}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Comparison Table */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Typography variant="h4" fontWeight="800" textAlign="center" gutterBottom>
            Why Hosts Choose FreshSweeper
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 24 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "12px" }}>Feature</th>
                  <th style={{ textAlign: "center", padding: "12px" }}>FreshSweeper</th>
                  <th style={{ textAlign: "center", padding: "12px" }}>Traditional</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: "Set Your Own Price", fresh: "✅", other: "❌" },
                  { feature: "Auto-Scheduling", fresh: "✅", other: "⚠️" },
                  { feature: "Photo Verification", fresh: "✅", other: "❌" },
                  { feature: "In-App Messaging", fresh: "✅", other: "⚠️" },
                  { feature: "Multi-Property Dashboard", fresh: "✅", other: "⚠️" },
                  { feature: "Flexible Payouts", fresh: "✅", other: "❌" },
                  { feature: "Platform Fee", fresh: "5%", other: "15-20%" },
                ].map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: idx < 6 ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : "none" }}>
                    <td style={{ padding: "12px", fontWeight: 600 }}>{row.feature}</td>
                    <td style={{ padding: "12px", textAlign: "center", color: theme.palette.success.main }}>{row.fresh}</td>
                    <td style={{ padding: "12px", textAlign: "center", color: theme.palette.error.main }}>{row.other}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Paper>
      </Container>

      {/* CTA */}
      <Box
        sx={{
          py: 8,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          borderRadius: { xs: 0, md: 4 },
          mx: { xs: 0, md: 2 },
          mb: 4,
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight="800" color="white" gutterBottom>
            Ready to Transform Your Hosting Business?
          </Typography>
          <Typography variant="body1" sx={{ color: alpha(theme.palette.common.white, 0.9), mb: 3 }}>
            Join thousands of hosts using FreshSweeper to automate their turnovers and maximize revenue.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AutoAwesomeIcon />}
            sx={{
              bgcolor: "white",
              color: theme.palette.primary.main,
              borderRadius: "50px",
              px: 5,
              py: 1.5,
              fontWeight: 700,
              "&:hover": {
                bgcolor: alpha(theme.palette.common.white, 0.9),
                transform: "translateY(-2px)",
              },
            }}
          >
            Start Free Trial
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default FeaturesOverview;